'use client';

import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface RecordDetails {
    name: string;
    /** A contact's companies / manufacturers, shown first. Optional so
     *  existing loaders keep compiling. */
    companies?: string[] | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    email: string | null;
    record_subtype: string | null;
    tags: string[] | null;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const detailsCache = new Map<string, { data: RecordDetails; ts: number }>();

/**
 * Loads one record's details for the popover. The app supplies it through
 * RecordDetailsLoaderProvider, so the element never calls an API itself.
 * Resolve null when the record can't be loaded.
 */
export type RecordDetailsLoader = (recordId: string) => Promise<RecordDetails | null>;

const RecordDetailsLoaderContext = createContext<RecordDetailsLoader | null>(null);

export const RecordDetailsLoaderProvider: React.FC<{ loader: RecordDetailsLoader; children: React.ReactNode }> = ({ loader, children }) => (
    <RecordDetailsLoaderContext.Provider value={loader}>{children}</RecordDetailsLoaderContext.Provider>
);

export interface RecordChipPopoverProps {
    recordId: string;
    displayName: string;
    children: React.ReactNode;
    delay?: number;
    /**
     * Overrides the trigger wrapper's layout. It defaults to `inline-flex`,
     * which is right for a chip sitting in a line of text but collapses a
     * full-width child (e.g. a list row) to its content width.
     */
    wrapperStyle?: React.CSSProperties;
}

export const RecordChipPopover: React.FC<RecordChipPopoverProps> = ({ recordId, displayName, children, delay = 500, wrapperStyle }) => {
    const [mounted, setMounted] = useState(false);
    const [show, setShow] = useState(false);
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number; flipBelow: boolean; shiftLeft: number } | null>(null);
    const [details, setDetails] = useState<RecordDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const loader = useContext(RecordDetailsLoaderContext);

    useEffect(() => { setMounted(true); }, []);

    const fetchDetails = useCallback(async () => {
        if (recordId.startsWith('unresolved:')) return;

        const cached = detailsCache.get(recordId);
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
            setDetails(cached.data);
            return;
        }

        setLoading(true);
        setError(false);
        try {
            const d = loader ? await loader(recordId) : null;
            if (!d) {
                setError(true);
                setLoading(false);
                return;
            }
            detailsCache.set(recordId, { data: d, ts: Date.now() });
            setDetails(d);
        } catch {
            setError(true);
        }
        setLoading(false);
    }, [recordId, loader]);

    const clearHide = () => {
        if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    };

    const startHide = () => {
        if (timer.current) { clearTimeout(timer.current); timer.current = null; }
        setVisible(false);
        hideTimer.current = setTimeout(() => { setShow(false); setPos(null); }, 200);
    };

    const onTriggerEnter = (e: React.MouseEvent) => {
        e.stopPropagation();
        clearHide();
        timer.current = setTimeout(() => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            // Flip below if less than 180px above trigger (estimated popover height)
            const flipBelow = rect.top < 180;
            // Shift left if popover would overflow the right edge (estimated 240px wide + 16px padding)
            const popoverWidth = 240;
            const rightOverflow = rect.left + popoverWidth - window.innerWidth + 16;
            const shiftLeft = rightOverflow > 0 ? rightOverflow : 0;
            setPos({ top: rect.top, left: rect.left, flipBelow, shiftLeft });
            setShow(true);
            fetchDetails();
            requestAnimationFrame(() => setVisible(true));
        }, delay);
    };

    const onTriggerLeave = () => {
        startHide();
    };

    useEffect(() => {
        return () => {
            if (timer.current) clearTimeout(timer.current);
            if (hideTimer.current) clearTimeout(hideTimer.current);
        };
    }, []);

    const hasContent = details && (details.companies?.length || details.address || details.city || details.state || details.zip || details.phone || details.email || details.record_subtype || details.tags?.length);

    const popover = mounted && show && pos ? createPortal(
        <div
            onMouseEnter={clearHide}
            onMouseLeave={startHide}
            style={{
                position: 'fixed',
                ...(pos.flipBelow
                    ? { top: pos.top + 30 }
                    : { top: pos.top - 6 }
                ),
                left: pos.left - pos.shiftLeft,
                transform: pos.flipBelow
                    ? (visible ? 'scaleY(1)' : 'translateY(-8px) scaleY(0.95)')
                    : (visible ? 'translateY(-100%) scaleY(1)' : 'translateY(-100%) translateY(8px) scaleY(0.95)'),
                transformOrigin: pos.flipBelow ? 'top left' : 'bottom left',
                background: 'var(--n-900)',
                borderRadius: 10,
                padding: '10px 14px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                whiteSpace: 'nowrap',
                zIndex: 9999,
                minWidth: 200,
                opacity: visible ? 1 : 0,
                transition: 'opacity 200ms cubic-bezier(0.16,1,0.3,1), transform 200ms cubic-bezier(0.16,1,0.3,1)',
                fontFamily: 'var(--font-sans)',
                color: 'var(--n-0)',
                pointerEvents: visible ? 'auto' : 'none',
            }}
        >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                @{displayName}
            </div>

            {loading && !details && (
                <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                        <SkeletonRow label="Address" width={120} delay={0} />
                        <SkeletonRow label="City" width={80} delay={60} />
                        <SkeletonRow label="St / Province" width={50} delay={120} />
                        <SkeletonRow label="Postal / Zip" width={60} delay={180} />
                    </tbody>
                </table>
            )}

            {error && !details && (
                <div style={{ fontSize: 12, color: 'var(--n-400)' }}>Record not available</div>
            )}

            {details && hasContent && (
                <>
                    <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            {details.companies && details.companies.length > 0 && (
                                <AttrRow label={details.companies.length > 1 ? 'Companies' : 'Company'} value={details.companies.join(', ')} />
                            )}
                            {details.address && <AttrRow label="Address" value={details.address} />}
                            {details.city && <AttrRow label="City" value={details.city} />}
                            {details.state && <AttrRow label="St / Province" value={details.state} />}
                            {details.zip && <AttrRow label="Postal / Zip" value={details.zip} />}
                            {details.phone && <AttrRow label="Phone" value={details.phone} />}
                            {details.email && <AttrRow label="Email" value={details.email} />}
                        </tbody>
                    </table>

                    {(details.record_subtype || (details.tags && details.tags.length > 0)) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                            {details.record_subtype && <TagBadge label={details.record_subtype} />}
                            {details.tags?.map(tag => <TagBadge key={tag} label={tag} />)}
                        </div>
                    )}
                </>
            )}

            {details && !hasContent && (
                <div style={{ fontSize: 12, color: 'var(--n-400)' }}>No details</div>
            )}
        </div>,
        document.body,
    ) : null;

    return (
        <span
            ref={triggerRef}
            style={{ display: 'inline-flex', ...wrapperStyle }}
            onMouseEnter={onTriggerEnter}
            onMouseLeave={onTriggerLeave}
            onClick={(e) => e.stopPropagation()}
        >
            {children}
            {popover}
        </span>
    );
};

function AttrRow({ label, value }: { label: string; value: string }) {
    return (
        <tr>
            <td style={{ color: 'var(--n-400)', fontWeight: 500, paddingRight: 16, paddingTop: 3, paddingBottom: 3, verticalAlign: 'top' }}>
                {label}
            </td>
            <td style={{ color: 'var(--n-0)', fontWeight: 500, paddingTop: 3, paddingBottom: 3 }}>
                {value}
            </td>
        </tr>
    );
}

function SkeletonRow({ label, width, delay }: { label: string; width: number; delay: number }) {
    return (
        <tr>
            <td style={{ color: 'var(--n-400)', fontWeight: 500, paddingRight: 16, paddingTop: 3, paddingBottom: 3, verticalAlign: 'top' }}>
                {label}
            </td>
            <td style={{ paddingTop: 3, paddingBottom: 3 }}>
                <span style={{
                    display: 'inline-block',
                    width,
                    height: 14,
                    borderRadius: 4,
                    background: 'var(--n-700)',
                    animation: 'skeletonPulse 1.5s ease-in-out infinite',
                    animationDelay: `${delay}ms`,
                }} />
            </td>
        </tr>
    );
}

function TagBadge({ label }: { label: string }) {
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            background: 'var(--n-800)',
            color: 'var(--n-200)',
            border: '1px solid var(--n-700)',
        }}>
            {label}
        </span>
    );
}
